import useSelectionStore from '@/stores/selection';
import useUiPlanningStore from '@/stores/ui/planning';

let isSelecting = false;
let startX;
let startY;
let selectionBox;
let tolerance = 'touch'; // 'fit' or 'touch'
let hasMovedEnoughToSelect = false;
let lastMoveTime = 0;
let pendingSelection = null;
let elementPositionCache = null;
let selectableAreaRectCache = null;
const THROTTLE_MS = 40; // 25fps max

// Use cache to test if element is in selection rectangle
function isElementInSelection(elementObj, selectionRect) {
  if (! selectionRect) return false;
  if (tolerance === 'fit') {
    // element must be entirely within the selection
    return elementObj.left >= selectionRect.left && elementObj.right <= selectionRect.right
           && elementObj.top >= selectionRect.top && elementObj.bottom <= selectionRect.bottom;
  }
  // element must just touch the selection
  return ! (elementObj.right < selectionRect.left || elementObj.left > selectionRect.right
            || elementObj.bottom < selectionRect.top || elementObj.top > selectionRect.bottom);
}

function batchUpdateSelectionClasses(elementList, selectedSet) {
  // batch DOM updates for performance
  elementList.forEach(({ element }) => {
    if (selectedSet.has(element)) {
      element.classList.add('ui-selecting');
    } else {
      element.classList.remove('ui-selecting');
    }
  });
}

export default {
  data() {
    return {
      selectionStore: useSelectionStore(),
      uiPlanningStore: useUiPlanningStore(),
    };
  },
  methods: {
    selectableAreaConfig() {
      if (! this.dynamic) return { enabled: false };
      const vm = this;
      return {
        enabled: true,
        filter: '.element.selectable',
        start(event, $selectableAreaEl) {
          if (! event.target.classList.contains('lane')) return;
          this.cleanup();

          if (document.activeElement) document.activeElement.blur();

          const selectableAreaRect = $selectableAreaEl.getBoundingClientRect();
          selectableAreaRectCache = selectableAreaRect; // cache for move events
          startX = event.clientX - selectableAreaRect.left;
          startY = event.clientY - selectableAreaRect.top;
          isSelecting = true;
          hasMovedEnoughToSelect = false;
          pendingSelection = null;

          // set tolerance to fit when shift+click
          tolerance = event.shiftKey ? 'fit' : 'touch';

          // create selection box
          selectionBox = document.createElement('div');
          selectionBox.style.cssText = `position: absolute;border: 1px dashed #007cba;background: rgba(0, 124, 186, 0.1);pointer-events: none;z-index: 9999;`;
          $selectableAreaEl.appendChild(selectionBox);

          // cache elements positions
          elementPositionCache = [];
          $selectableAreaEl.querySelectorAll(this.filter || '*').forEach((element) => {
            const rect = element.getBoundingClientRect();
            elementPositionCache.push({
              element,
              left: rect.left - selectableAreaRect.left,
              top: rect.top - selectableAreaRect.top,
              right: rect.right - selectableAreaRect.left,
              bottom: rect.bottom - selectableAreaRect.top,
            });
          });

          if (! event.ctrlKey) {
            vm.selectionStore.reset();
          }
        },
        move(event, $selectableAreaEl) {
          if (! isSelecting) return;
          const now = Date.now();
          if (now - lastMoveTime < THROTTLE_MS) return;
          lastMoveTime = now;

          const selectableAreaRect = selectableAreaRectCache || $selectableAreaEl.getBoundingClientRect();
          const currentX = event.clientX - selectableAreaRect.left;
          const currentY = event.clientY - selectableAreaRect.top;

          if (! hasMovedEnoughToSelect) {
            if (Math.abs(currentX - startX) < 5 && Math.abs(currentY - startY) < 5) return; // not moved enough yet (distance: 5)
            hasMovedEnoughToSelect = true;
          }
          if (selectionBox) {
            selectionBox.style.left = `${Math.min(startX, currentX)}px`;
            selectionBox.style.top = `${Math.min(startY, currentY)}px`;
            selectionBox.style.width = `${Math.abs(currentX - startX)}px`;
            selectionBox.style.height = `${Math.abs(currentY - startY)}px`;
          }

          // calculation of the selection rectangle in the container's coordinate system
          const selectionRect = {
            left: Math.min(startX, currentX),
            top: Math.min(startY, currentY),
            right: Math.max(startX, currentX),
            bottom: Math.max(startY, currentY),
          };

          // batch selection calculation with cache
          const selectedSet = new Set();
          if (elementPositionCache) {
            elementPositionCache.forEach((elementObj) => {
              if (isElementInSelection(elementObj, selectionRect)) selectedSet.add(elementObj.element);
            });
          }
          pendingSelection = { elementPositionCache, selectedSet };
          // batch DOM update only every THROTTLE_MS
          batchUpdateSelectionClasses(elementPositionCache, selectedSet);
        },
        end(event, $selectableAreaEl) {
          if (! isSelecting) return;
          isSelecting = false;
          if (hasMovedEnoughToSelect) {
            // batch DOM update one last time
            if (pendingSelection) {
              batchUpdateSelectionClasses(pendingSelection.elementPositionCache, pendingSelection.selectedSet);
            }
            // calculate the final selection rectangle
            const selectableAreaRect = $selectableAreaEl.getBoundingClientRect();
            const currentX = event.clientX - selectableAreaRect.left;
            const currentY = event.clientY - selectableAreaRect.top;
            const selectionRect = {
              left: Math.min(startX, currentX),
              top: Math.min(startY, currentY),
              right: Math.max(startX, currentX),
              bottom: Math.max(startY, currentY),
            };
            const selectedElements = [];
            if (elementPositionCache) {
              elementPositionCache.forEach((elementObj) => {
                elementObj.element.classList.remove('ui-selecting');
                if (isElementInSelection(elementObj, selectionRect)) selectedElements.push(elementObj.element);
              });
            }
            const newSelection = selectedElements.map($el => parseInt($el.id.replace('el', ''), 10));
            vm.selectionStore.reset(newSelection);
            vm.uiPlanningStore.doNotResetSelection = true; // prevent mouseup from clearing selection
            setTimeout(() => { vm.uiPlanningStore.doNotResetSelection = false; });
          }

          // clean up
          if (selectionBox) {
            selectionBox.remove();
            selectionBox = null;
          }
          pendingSelection = null;
          elementPositionCache = null;
        },
        cleanup() {
          if (selectionBox) selectionBox.remove();
          pendingSelection = null;
        },
      };
    },

    selectableClick(event, el) {
      if (this.uiPlanningStore.doNotUpdateSelection) return;
      if (! this.isDynamic(el)) return;
      const { id } = el;
      if (event.ctrlKey) { // Add/remove to/from selection
        if (! this.selectionStore.selection.has(id)) {
          this.selectionStore.selection.add(id);
        } else {
          this.selectionStore.selection.delete(id);
        }
      } else {
        this.selectionStore.reset([id]);
      }
    },
  },
};
